import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "../../lib/api";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";

const FIELDS = [
  { key: "airport",     label: "Airport Name",  placeholder: "e.g. Indira Gandhi International Airport" },
  { key: "iata_code",   label: "IATA Code",     placeholder: "e.g. DEL",   upper: true, maxLength: 10 },
  { key: "city_region", label: "City / Region", placeholder: "e.g. Delhi" },
  { key: "country",     label: "Country",       placeholder: "e.g. India" },
];

const DEFAULT_FORM = {
  airport:     "",
  iata_code:   "",
  city_region: "",
  country:     "",
  is_active:   true,
};

function useSuggestions(query, enabled) {
  const [items, setItems] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    if (!enabled) { setItems([]); return; }
    const q = String(query || "").trim();
    if (!q) { setItems([]); return; }

    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const res = await api.get("/admin/popular-destinations/airports/search", {
          params: { q },
        });
        setItems(res.data?.data || []);
      } catch {
        setItems([]);
      }
    }, 200);

    return () => clearTimeout(timer.current);
  }, [query, enabled]);

  return [items, setItems];
}

function SuggestionDropdown({ items, onPick, fieldKey }) {
  if (!items.length) return null;

  const highlight = (text, field) => {
    const val = text?.[field] || "";
    return val;
  };

  return (
    <ul className="absolute left-0 right-0 top-full mt-1 max-h-52 overflow-auto rounded-xl border bg-white shadow-xl z-50 text-sm">
      {items.map((item, i) => (
        <li key={i}>
          <button
            type="button"
            className="w-full text-left px-3 py-2 hover:bg-gray-100 flex flex-col"
            onMouseDown={(e) => { e.preventDefault(); onPick(item); }}
          >
            <span className="font-semibold text-gray-900">{highlight(item, fieldKey)}</span>
            <span className="text-xs text-gray-500 truncate">
              {item.iata_code} · {item.city_region} · {item.country}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function PopularDestinationUpsertPage() {
  const navigate  = useNavigate();
  const { id }    = useParams();
  const isEdit    = Boolean(id);

  const [form, setForm]       = useState(DEFAULT_FORM);
  const [nextRank, setNextRank] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [focusKey, setFocusKey] = useState(null);

  const [suggestions, setSuggestions] = useSuggestions(
    focusKey ? form[focusKey] : "",
    Boolean(focusKey)
  );

  useEffect(() => {
    if (!isEdit) {
      api.get("/admin/popular-destinations/next-rank")
        .then((res) => setNextRank(res.data?.next_rank ?? "—"))
        .catch(() => setNextRank("—"));
    }
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/popular-destinations/${id}`)
      .then((res) => {
        const d = res.data?.data;
        if (!d) return;
        setNextRank(d.rank);
        setForm({
          airport:     d.airport     || "",
          iata_code:   d.iata_code   || "",
          city_region: d.city_region || "",
          country:     d.country     || "",
          is_active:   d.is_active !== undefined ? d.is_active : true,
        });
      })
      .catch(() => toast.error("Failed to load record"));
  }, [id, isEdit]);

  const pickSuggestion = useCallback((item) => {
    setForm((s) => ({
      ...s,
      airport:     item.airport     || s.airport,
      iata_code:   item.iata_code   || s.iata_code,
      city_region: item.city_region || s.city_region,
      country:     item.country     || s.country,
    }));
    setSuggestions([]);
    setFocusKey(null);
  }, [setSuggestions]);

  const handleBlur = useCallback(() => {
    setTimeout(() => setFocusKey(null), 160);
  }, []);

  const handleChange = useCallback((key, value, upper) => {
    const val = upper ? value.toUpperCase() : value;
    setForm((s) => ({ ...s, [key]: val }));
  }, []);

  const validate = () => {
    if (!form.airport.trim())     return "Airport Name is required";
    if (!form.iata_code.trim())   return "IATA Code is required";
    if (!form.city_region.trim()) return "City / Region is required";
    if (!form.country.trim())     return "Country is required";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const err = validate();
    if (err) return toast.error(err);

    const payload = {
      airport:     form.airport.trim(),
      iata_code:   form.iata_code.trim().toUpperCase(),
      city_region: form.city_region.trim(),
      country:     form.country.trim(),
      is_active:   form.is_active,
    };

    if (isEdit) {
      payload.rank = nextRank;
    }

    setSaving(true);
    try {
      if (!isEdit) {
        await api.post("/admin/popular-destinations", payload);
        toast.success("Destination created");
      } else {
        await api.put(`/admin/popular-destinations/${id}`, payload);
        toast.success("Destination updated");
      }
      navigate("/admin/popular-destinations");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-xl">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold">
          {isEdit ? "Edit Popular Destination" : "Add Popular Destination"}
        </h1>
        <button
          type="button"
          onClick={() => navigate("/admin/popular-destinations")}
          className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50 self-start"
        >
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Rank — always readonly / auto */}
        <div>
          <label className="text-sm font-semibold">Rank</label>
          <div className="mt-2 w-full border rounded-xl px-3 py-2 bg-gray-50 text-gray-500 text-sm flex items-center gap-2">
            <span className="font-bold text-gray-800">{nextRank ?? "…"}</span>
            <span className="text-xs">{isEdit ? "(current rank)" : "(auto-assigned)"}</span>
          </div>
        </div>

        {/* Smart suggestion fields */}
        {FIELDS.map(({ key, label, placeholder, upper, maxLength }) => (
          <div key={key}>
            <label className="text-sm font-semibold">
              {label} <span className="text-red-500">*</span>
            </label>
            <div className="relative mt-2">
              <input
                className={`w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black ${upper ? "uppercase" : ""}`}
                placeholder={placeholder}
                value={form[key]}
                maxLength={maxLength}
                autoComplete="off"
                onChange={(e) => handleChange(key, e.target.value, upper)}
                onFocus={() => setFocusKey(key)}
                onBlur={handleBlur}
              />
              {focusKey === key && (
                <SuggestionDropdown
                  items={suggestions}
                  onPick={pickSuggestion}
                  fieldKey={key}
                />
              )}
            </div>
          </div>
        ))}

        <div className="flex items-center gap-3">
          <label className="text-sm font-semibold">Active</label>
          <input
            type="checkbox"
            className="w-4 h-4"
            checked={form.is_active}
            onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => navigate("/admin/popular-destinations")}
            className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold disabled:opacity-60"
          >
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create"}
          </button>
        </div>
      </form>
    </div>
  );
}
